import type { FileType } from '@unifyapps/defs/blocks/FileUpload/types';
import type { EmitEventType } from '@unifyapps/defs/hooks/useBlockEvents';
import type { BlockStateUnionType } from '@unifyapps/defs/types/block';
import type { JavaScriptFunctionState } from '@unifyapps/defs/types/javascriptFunction';
import type { JavaScriptVariableState } from '@unifyapps/defs/types/javascriptVariable';
import type { Draft } from 'immer';
import type { RefObject } from 'react';
import { create, type StateCreator, type StoreApi } from 'zustand';
import type { RemountFunction } from '../../../components/Remount/Remount';
import createObjectSetters from '../../../utils/setters/createObjectSetters';
import { createTapBinder } from '../../pulse/createTapBinder';
import type { ScopeId } from '../../pulse/types';
import createDataSourceControllerSlice from '../../slices/DataSourceControllerSlice/createDataSourceControllerSlice';
import { materializeInstanceState } from '../../utils/repeatables/blockStateMutator';
import { parseRuntimeBlockId } from '../../utils/repeatables/parseRuntimeBlockId';
import type { DataSourceRecordStoreState } from '../DataSourceRecordStore';
import type {
  BlockLifecycleStatus,
  GlobalStateStore,
  GlobalStateStoreInitialValue,
  GlobalStateStoreType,
} from './types';

export const createGlobalStateStore = ({
  initial,
  getDataSourceRecordsStore,
  remount,
  scopeId,
  capture,
}: {
  initial: GlobalStateStoreInitialValue;
  getDataSourceRecordsStore: StoreApi<DataSourceRecordStoreState>['getState'];
  remount: RemountFunction;
  scopeId: ScopeId;
  // false for RUNNER-mode page trees so their setters never feed Pulse — see createTapBinder.
  capture: boolean;
}) => {
  const initializer: StateCreator<GlobalStateStoreType> = (set, get) => {
    const {
      setSingle: rawSetBlockState,
      updateSingle: rawUpdateBlockState,
      setMultiple: rawSetBlocksState,
      removeMultiple: removeBlocksState,
    } = createObjectSetters<GlobalStateStoreType, 'blocks', BlockStateUnionType>('blocks', set);

    // Repeatable instance state is materialized lazily on first write. The store
    // self-references for parent config — top-level block IDs (`b_*`) carry their
    // authoring-time config in `blocks` already, so we don't need a separate config
    // source. See packages/carbon/src/no-code/utils/blockStateMutator.ts.
    const getParentConfigFromStore = (originalBlockId: string) => get().blocks[originalBlockId];

    // Single gate used by every Repeatable-aware setter. Non-instance ids pass
    // through unchanged; instance ids materialize against the current snapshot,
    // falling back to the raw value when the synthesizer has nothing to add.
    const materializeOrPassthrough = (
      id: string,
      value: BlockStateUnionType,
      currentBlocks: Record<string, BlockStateUnionType | undefined>,
    ): BlockStateUnionType => {
      const parsed = parseRuntimeBlockId(id);
      if (!parsed.isInstance) return value;
      const materialized = materializeInstanceState(
        id,
        value,
        currentBlocks,
        getParentConfigFromStore,
      );
      return materialized ?? value;
    };

    const setBlockState = (id: string, value: BlockStateUnionType) => {
      rawSetBlockState(id, materializeOrPassthrough(id, value, get().blocks));
    };

    const setBlocksState = (newValues: Partial<Record<string, BlockStateUnionType>>) => {
      const currentBlocks = get().blocks;
      const processed: Partial<Record<string, BlockStateUnionType>> = {};
      for (const [id, value] of Object.entries(newValues)) {
        if (!value) {
          processed[id] = value;
          continue;
        }
        processed[id] = materializeOrPassthrough(id, value, currentBlocks);
      }
      rawSetBlocksState(processed);
    };

    // updateBlockState uses Immer drafts. For an untouched runtime instance, no entry
    // exists in `blocks` — the draft would be `undefined` and the mutator would crash.
    // We seed the entry with the synthesized state first, then run the user's mutator.
    const updateBlockState = <PayloadType extends Record<string, unknown>>(
      id: string,
      mutator: (draft: Draft<PayloadType>) => void,
    ) => {
      const parsed = parseRuntimeBlockId(id);
      const wasMissing = parsed.isInstance && !get().blocks[id];
      if (wasMissing) {
        const seed = materializeInstanceState(id, {}, get().blocks, getParentConfigFromStore);
        if (seed) {
          rawSetBlockState(id, seed);
        }
      }
      rawUpdateBlockState(id, mutator);
    };

    const {
      setSingle: setPageVariableState,
      setMultiple: setPageVariablesState,
      remove: rawRemovePageVariableState,
    } = createObjectSetters<GlobalStateStoreType, 'pageVariables', JavaScriptVariableState>(
      'pageVariables',
      set,
    );

    // Zero-overhead pulse: taps are wired ONLY when pulse is enabled (decided once, at app entry).
    // When off, getTaps() is undefined and the RAW setters are used directly — no wrapper frame, no
    // per-call check, nothing for the runner to pay. (T3: replaces the old always-wrap design.)
    const tap = createTapBinder(scopeId, get, capture);
    const instIdOf = (id: string) => {
      const parsed = parseRuntimeBlockId(id);
      return parsed.isInstance ? parsed.instanceKey : undefined;
    };
    const tSetBlockState = tap.mapSetter('block', 'blocks', setBlockState, instIdOf);
    const tSetBlocksState = tap.multiSetter('block', 'blocks', setBlocksState, instIdOf);
    const tUpdateBlockState = tap.updater('block', 'blocks', updateBlockState, instIdOf);
    const tSetPageVariableState = tap.mapSetter(
      'pageVariable',
      'pageVariables',
      setPageVariableState,
    );
    const tSetPageVariablesState = tap.multiSetter(
      'pageVariable',
      'pageVariables',
      setPageVariablesState,
    );
    const tRemovePageVariableState = tap.mapRemover(
      'pageVariable',
      'pageVariables',
      rawRemovePageVariableState,
    );
    const tRemoveBlocksState = tap.mapMultiRemover('block', 'blocks', removeBlocksState, instIdOf);

    const { setSingle: rawSetPageFunctionState, remove: rawRemovePageFunctionState } =
      createObjectSetters<GlobalStateStoreType, 'pageFunctions', JavaScriptFunctionState>(
        'pageFunctions',
        set,
      );
    const setPageFunctionState = tap.mapSetter(
      'pageFunction',
      'pageFunctions',
      rawSetPageFunctionState,
    );
    const removePageFunctionState = tap.mapRemover(
      'pageFunction',
      'pageFunctions',
      rawRemovePageFunctionState,
    );

    const { setSingle: setBlockLifecycle } = createObjectSetters<
      GlobalStateStoreType,
      'blockLifecycles',
      BlockLifecycleStatus
    >('blockLifecycles', set);

    const { setSingle: setBlockRef, remove: removeBlockRef } = createObjectSetters<
      GlobalStateStoreType,
      'blockRefs',
      RefObject<unknown>
    >('blockRefs', set);

    const {
      setSingle: setMediaUploads,
      setMultiple: setMultipleMediaUploads,
      remove: removeMediaUploads,
    } = createObjectSetters<GlobalStateStoreType, 'mediaUploads', FileType[]>('mediaUploads', set);

    const { setSingle: setDraggableData } = createObjectSetters<
      GlobalStateStoreType,
      'draggableData',
      Record<string, unknown>
    >('draggableData', set);

    const setPreBuiltFlow: GlobalStateStoreType['actions']['setPreBuiltFlow'] = ({
      data,
      type,
    }) => {
      set((state) => {
        return {
          ...state,
          flows: {
            ...state.flows,
            [type]: data,
          },
        };
      });
    };

    const removePreBuiltFlow: GlobalStateStoreType['actions']['removePreBuiltFlow'] = (type) => {
      set((state) => {
        return {
          ...state,
          flows: {
            ...state.flows,
            [type]: undefined,
          },
        };
      });
    };

    const resetBlockRefs = () => {
      set((state) => {
        return {
          ...state,
          blockRefs: {},
        };
      });
    };

    const setIsInitialized = () => {
      set((state) => {
        return {
          ...state,
          isInitialized: true,
        };
      });
    };

    const setDispatchPageEvent = (dispatchPageEvent: EmitEventType | undefined) => {
      set(() => ({ dispatchPageEvent }));
    };

    const setBlockSelectionActive = (active: boolean) => {
      set(() => ({ blockSelectionActive: active }));
    };

    const reset = () => {
      set(() => storeObject);
    };

    const setContextualDialogForBlocks = (
      contextualDialogForBlocks: Record<string, string | undefined>,
    ) => {
      set((state) => {
        return {
          ...state,
          contextualDialogForBlocks,
        };
      });
    };

    const { dataSourceControllerState, dataSourceControllerActions } =
      createDataSourceControllerSlice({
        get,
        set,
        getDataSourceRecordsStore,
        scopeId,
        capture,
      });

    const state: GlobalStateStore = {
      blockLifecycles: {},
      flows: {},
      blockRefs: {},
      mediaUploads: {},
      draggableData: {},
      draggableBlockIds: [],
      ...dataSourceControllerState,
      ...initial,
    };

    const storeObject: GlobalStateStoreType = {
      ...state,
      actions: {
        setBlocksState: tSetBlocksState,
        removeBlocksState: tRemoveBlocksState,
        setBlockState: tSetBlockState,
        updateBlockState: tUpdateBlockState,
        setBlockLifecycle,
        dataSource: dataSourceControllerActions,
        setBlockRef,
        resetBlockRefs,
        removeBlockRef,
        setMediaUploads,
        setMultipleMediaUploads,
        removeMediaUploads,
        setDraggableData,
        setDraggableBlockIds: (blockIds) => {
          set({ draggableBlockIds: blockIds });
        },
        setBlockSelection: (blockSelection) => {
          set({ blockSelection });
        },
        setPageInputsState: (pageInputs) => {
          set({ pageInputs });
        },
        setPageParamsState: (pageParams) => {
          set({ pageParams });
        },
        setPageVariableState: tSetPageVariableState,
        setPageVariablesState: tSetPageVariablesState,
        removePageVariableState: tRemovePageVariableState,
        setPageFunctionState,
        removePageFunctionState,
        setContextualDialogForBlocks,
        setPreBuiltFlow,
        removePreBuiltFlow,
        setDispatchPageEvent,
        setIsInitialized,
        reset,
        remount,
        setBlockSelectionActive,
      },
    };

    return storeObject;
  };
  return create<GlobalStateStoreType>()(initializer);
};
